import { Box, Button, Stack, Typography } from '@mui/material'
import * as monaco from 'monaco-editor'
import { useCallback, useState } from 'react'

import { RecipeTextEditor, useConfirm } from '@/components'
import {
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'

import IModelDecoration = monaco.editor.IModelDecoration
import { JSONSchemaType } from 'ajv'

import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useSchema } from '@/hooks'
import { safeCypressID } from '@/utils'

export type CustomComponentData = {
  data: string
  name: string
}

type TextEditorState = {
  errors: IModelDecoration[]
  text: string
}

export type ComponentEditorProps = {
  schemaName: string
  schemaVersion: string
  generateSchema: (
    initialSchema: JSONSchemaType<unknown>
  ) => JSONSchemaType<unknown>
}
export default function RecipeEditor({
  schemaName,
  schemaVersion,
  generateSchema,
}: ComponentEditorProps) {
  const schema = useSchema({
    name: schemaName,
    version: schemaVersion,
  })
  const { componentID } = useComponentProvider()
  const component = useGetCustomComponent<CustomComponentData>(componentID)
  const updateComponent =
    useUpdateCustomComponent<CustomComponentData>(componentID)

  const [config, setConfig] = useState<TextEditorState>({
    errors: [],
    text: component.data?.settings?.data ?? '',
  })

  const confirm = useConfirm()
  const saveCustomComponent = async () => {
    try {
      console.log(config.errors)
      if (config.errors.length !== 0)
        await confirm({
          confirmationButtonProps: { color: 'primary', variant: 'contained' },
          content: (
            <>
              <Typography>
                {config.errors.length > 1
                  ? 'There are some errors in your config'
                  : 'There is an error in your config'}
              </Typography>
              <Stack>
                {config.errors.map((err) => (
                  <Typography key={err.id} variant="caption">
                    {/* @ts-ignore */}
                    {config.text.slice(err?.start - 1, err?.end)}
                  </Typography>
                ))}
              </Stack>
              <Typography>Are you sure that you want to save? </Typography>
            </>
          ),
          title: 'Save Config',
        })
      await updateComponent.mutateAsync({
        id: componentID,
        settings: {
          data: config.text,
        },
      })
    } catch (e) {
      console.log(e)
    }
  }

  const onChange = useCallback(
    ({ errors, text }: { text: string; errors: IModelDecoration[] }) => {
      setConfig({ errors, text })
    },
    []
  )
  if (schema.isLoading) return null
  return (
    <Box sx={{ flex: 1, position: 'relative' }}>
      <Button
        id={safeCypressID('save component code')}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          transform: 'translateY(-36px)',
        }}
        variant="contained"
        onClick={saveCustomComponent}
      >
        Save
      </Button>
      <RecipeTextEditor
        schema={generateSchema(schema.data)}
        value={config.text}
        onChange={onChange}
      />
    </Box>
  )
}
